import React, { useEffect, useState } from "react";
import { Box, Stack, Button, Grid, Typography, CircularProgress } from "@mui/material";
import RSelect from "react-select";
import {
  getCampaignBillboardsAPI,
  getImpressionLineChartAPI,
  getImpressionScatterplotAPI,
  getImpressionDayOfWeekAPI,
  getImpressionMonthlyAPI
} from "../../apis/campaigns.apis";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";

import CampaignImpressionLineChart from "./CampaignImpressionLineChart";
import CampaignImpressionDayOfWeek from "./CampaignImpressionDayOfWeek";
import CampaignImpressionMonthly from "./CampaignImpressionMonthly";
import CampaignImpressionScatterplot from "./CampaignImpressionScatterplot";

const ImpressionBoxPlot = ({ campaignId }) => {
  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState({
    zone: null,
    state: null,
    city: null,
    location: null,
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const currentYear = new Date().getFullYear();
  const [selectedGraph, setSelectedGraph] = React.useState(null);
  const [graphData, setGraphData] = React.useState(null);

  const handleMonthChange = async (month) => {
    setSelectedMonth(month);
  };

  useEffect(() => {
    const fetchBillboards = async () => {
      try {
        const res = await getCampaignBillboardsAPI(campaignId);
        const result = res.data;
        setData(result);
        const zoneOptions = result.map((z) => ({
          label: z.zone_name,
          value: z.zone_id,
        }));
        setZones(zoneOptions);
      } catch (err) {
        console.error("Error fetching billboards:", err);
      }
    };
    fetchBillboards();
  }, [campaignId]);

  const graphConfigs = {
    lineChart: {
      api: getImpressionLineChartAPI,
      successMsg: "Impression Line Chart plotted successfully!",
      errorMsg: "No data available for impression line chart!",
    },
    scatterplot: {
      api: getImpressionScatterplotAPI,
      successMsg: "Impression Scatterplot plotted successfully!",
      errorMsg: "No data available for impression scatterplot!",
    },
    dayOfWeek: {
      api: getImpressionDayOfWeekAPI,
      successMsg: "Day of Week Impression Graph plotted successfully!",
      errorMsg: "No data available for day of week impression graph!",
    },
    monthly: {
      api: getImpressionMonthlyAPI,
      successMsg: "Monthly Impression Graph plotted successfully!",
      errorMsg: "No data available for monthly impression data!",
      payloadExtra: () => ({ month: selectedMonth, year: currentYear }),
    }
  };

  const handleShowGraph = async (graphType) => {
    const config = graphConfigs[graphType];
    if (!config) return;

    setIsLoading(true);
    setSelectedGraph(null);
    setGraphData(null);

    try {
      const payload = {
        campaign_id: campaignId,
        zone_id: selected.zone?.value || null,
        state_id: selected.state?.value || null,
        city_id: selected.city?.value || null,
        location: selected.location?.value || null,
        ...(config.payloadExtra ? config.payloadExtra() : {}),
      };

      const res = await config.api(payload);
      setSelectedGraph(graphType);
      if (res.success && res.data && Object.keys(res.data).length > 0) {
        setGraphData(res.data);
        toast.success(config.successMsg);
      } else {
        toast.error(config.errorMsg);
      }
    } catch {
      toast.error(`Failed to plot ${graphType} graph!`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (key, value) => {
    let newSelected = { ...selected, [key]: value };

    if (key === "zone") {
      const zone = data.find((z) => z.zone_id === value.value);
      const stateOptions =
        zone?.states.map((s) => ({
          label: s.state_name,
          value: s.state_id,
        })) || [];
      setStates(stateOptions);
      setCities([]);
      setLocations([]);
      newSelected = { zone: value, state: null, city: null, location: null };
    } else if (key === "state") {
      const zone = data.find((z) => z.zone_id === selected.zone?.value);
      const state = zone?.states.find((s) => s.state_id === value.value);
      const cityOptions =
        state?.cities.map((c) => ({
          label: c.city_name,
          value: c.city_id,
        })) || [];
      setCities(cityOptions);
      setLocations([]);
      newSelected = { ...selected, state: value, city: null, location: null };
    } else if (key === "city") {
      const zone = data.find((z) => z.zone_id === selected.zone?.value);
      const state = zone?.states.find(
        (s) => s.state_id === selected.state?.value
      );
      const city = state?.cities.find((c) => c.city_id === value.value);
      const locationOptions =
        city?.locations.map((l) => ({
          label: l,
          value: l,
        })) || [];
      setLocations(locationOptions);
      newSelected = { ...selected, city: value, location: null };
    } else if (key === "location") {
      newSelected = { ...selected, location: value };
    }

    setSelected(newSelected);
  };

  return (
    <Stack spacing={2}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
          <RSelect
            placeholder="Zone"
            options={zones}
            value={selected.zone}
            onChange={(v) => handleChange("zone", v)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <RSelect
            placeholder="State"
            options={
              states.length
                ? states
                : [{ label: "Select Zone First", value: 0 }]
            }
            value={selected.state}
            onChange={(v) => handleChange("state", v)}
            isDisabled={!selected.zone}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <RSelect
            placeholder="City"
            options={
              cities.length
                ? cities
                : [{ label: "Select State First", value: 0 }]
            }
            value={selected.city}
            onChange={(v) => handleChange("city", v)}
            isDisabled={!selected.state}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <RSelect
            placeholder="Location"
            options={
              locations.length
                ? locations
                : [{ label: "Select City First", value: 0 }]
            }
            value={selected.location}
            onChange={(v) => handleChange("location", v)}
            isDisabled={!selected.city}
          />
        </Grid>
      </Grid>

      <Box
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Button
          variant="contained"
          onClick={() => handleShowGraph("lineChart")}
          disabled={!selected.zone}
        >
          Show Impression Line Chart
        </Button>
        <Button
          variant="contained"
          onClick={() => handleShowGraph("scatterplot")}
          disabled={!selected.zone}
        >
          Show Impression Scatterplot
        </Button>
        <Button
          variant="contained"
          onClick={() => handleShowGraph("dayOfWeek")}
          disabled={!selected.zone}
        >
          Show Day of Week Analysis
        </Button>
        <Button
          variant="contained"
          onClick={() => handleShowGraph("monthly")}
          disabled={!selected.zone}
        >
          Show Monthly Analysis
        </Button>
      </Box>

      {isLoading && <Loader open={true} />}

      {selectedGraph === "lineChart" && graphData && (
        <CampaignImpressionLineChart data={graphData} />
      )}

      {selectedGraph === "scatterplot" && graphData && (
        <CampaignImpressionScatterplot data={graphData} />
      )}

      {selectedGraph === "dayOfWeek" && graphData && (
        <CampaignImpressionDayOfWeek data={graphData} />
      )}

      {selectedGraph === "monthly" && (
        <CampaignImpressionMonthly
          data={graphData || {}}
          selectedMonth={selectedMonth}
          onMonthChange={handleMonthChange}
        />
      )}
    </Stack>
  );
};

export default ImpressionBoxPlot;